import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { Translate } from 'react-localize-redux'
import { makeStyles } from '@material-ui/core/styles'
import { withRouter } from 'react-router-dom'
import {
  Button,
  IconButton,
  Paper,
  Tabs,
  Tab,
  Tooltip,
  Box,
  Grid,
  Hidden,
  Badge,
} from '@material-ui/core'
import ShareIcon from '@material-ui/icons/Share'
import LockIcon from '@material-ui/icons/Lock'
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline'
import HistoryIcon from '@material-ui/icons/History'
import { SubLink } from 'containers'
import { connect } from 'react-redux'
import { compose } from 'redux'
import { grey } from '@material-ui/core/colors'
import urlConstructor from 'utils/urlConstructor'
import useTeamFeature from 'hooks/useTeamFeature'
import usePermission from 'hooks/usePermission'
import PERMISSIONS from '../../../constants/permissions'
import RemovePlayerDialog from '../../../containers/TeamDashboard/components/TeamPlayersGrid/RemovePlayerDialog'
import { TEAM_PLAYER_TRANSFER_HISTORY_ROUTE } from 'constants/routes'
import TEAM_FEATURE_FLAGS from 'constants/teamFeatureFlags'

const useStyles = makeStyles((theme) => ({
  paper: {
    width: '100%',
    display: 'flex',
    padding: theme.spacing(0.3, 0.9, 0, 0),
  },
  Cancel: {
    margin: theme.spacing(0, 1.1),
  },
  PlayerNum: {
    margin: theme.spacing(0, 1.1),
    '& img': {
      marginRight: theme.spacing(0, 0.9),
    },
    '& :hover': {
      backgroundColor: 'transparent',
    },
  },
  newSessionDrill: {
    backgroundColor: theme.palette.primary.contrastText,
    padding: theme.spacing(0, 2),
    borderRadius: '6px',
  },
  headRight: {
    display: 'flex',
    width: '100%',
    alignItems: 'center',
    justifyContent: 'flex-end',
    '& .MuiDialog-paperWidthSm': {
      width: '100%',
    },
    [theme.breakpoints.down('sm')]: {
      position: 'fixed',
      bottom: 0,
      left: 0,
      background: '#fff',
      padding: theme.spacing(0.7),
      zIndex: 9,
      justifyContent: 'center',
      boxShadow: '14px 5px 35px 0 rgba(0, 0, 0, 0.08)',
    },
  },
  [theme.breakpoints.down('sm')]: {
    paper: {
      display: 'flex',
      flexDirection: 'column',
    },
    Cancel: {
      padding: theme.spacing(1, 3.7),
    },
    Save: {
      padding: theme.spacing(1, 3.7),
    },
    MobileSectionDialog: {
      '& .MuiDialog-paperWidthSm': {
        width: '100%',
      },
    },
    DialogClose: {
      position: 'absolute',
      top: 4,
      right: 52,
      zIndex: 10,
    },
    MobileSectionDialogBtn: {
      textAlign: 'center',
      display: 'flex',
      height: '4.375rem',
      position: 'fixed',
      boxShadow: '14px 5px 35px 0 rgba(0,0,0,0.08)',
      padding: '0.875rem 1.125rem',
      bottom: '0',
      left: '0',
      borderTop: `1px solid ${grey[400]}`,
      background: theme.palette.primary.contrastText,
      width: '100%',
      zIndex: '10',
      '& a': {
        width: '50%',
      },
      '& .MuiButton-root': {
        width: '100%',
      },
    },
  },
  // @todo Duplicate styling try to make a common badge component
  lockBadgeOnIconButton: {
    '& .MuiBadge-badge': {
      width: 12,
      height: 12,
      minWidth: 12,
      backgroundColor: theme.palette.background.paper,
    },
    '& .MuiBadge-anchorOriginTopRightCircular .MuiSvgIcon-root': {
      fontSize: 10,
    },
  },
}))

const TopBar = ({
  handleSave,
  deletePlayer,
  onShare,
  history,
  match: { params },
  currentClubUuid,
}) => {
  const classes = useStyles()
  const { teamUuid, seasonId, playerId } = params
  const [value, setValue] = React.useState(0)
  const [deleteIsOpen, setDeleteIsOpen] = useState(false)

  const { enabled: hasPlayerProfileExportEnabled } = useTeamFeature(
    TEAM_FEATURE_FLAGS.player_profile_export_enabled
  )

  const handleClose = () => {
    setDeleteIsOpen(false)
  }

  const handleDeleteClick = () => {
    setDeleteIsOpen(true)
  }

  const handleDeleteConfirm = () => {
    setDeleteIsOpen(false)
    deletePlayer()
  }

  const handleChange = (event, newValue) => {
    setValue(newValue)
  }

  const handlePlayerTransferHistoryIconClick = () => {
    history.push(
      urlConstructor(
        teamUuid,
        seasonId,
        TEAM_PLAYER_TRANSFER_HISTORY_ROUTE.populateURLParams({
          playerId,
        })
      )
    )
  }

  return (
    <>
      {deleteIsOpen && (
        <RemovePlayerDialog
          open={deleteIsOpen}
          onClose={handleClose}
          onSubmit={handleDeleteConfirm}
        />
      )}
      <Paper className={classes.paper}>
        <Grid item lg={6} md={6} sm={12} xs={12} align="left" display="flex">
          <Translate>
            {({ translate }) => (
              <Tabs
                value={value}
                onChange={handleChange}
                variant="scrollable"
                scrollButtons="auto"
              >
                <Tab label={translate('team.coachbetter-profile')} />
              </Tabs>
            )}
          </Translate>
        </Grid>

        <Grid item lg={6} md={6} sm={12} xs={12} align="right" display="flex">
          <Box className={classes.headRight}>
            <Tooltip
              title={<Translate id="button.share" />}
              placement="top-start"
              arrow
            >
              <IconButton onClick={onShare}>
                <Badge
                  className={classes.lockBadgeOnIconButton}
                  badgeContent={
                    !hasPlayerProfileExportEnabled ? (
                      <LockIcon color="primary" fontSize="small" />
                    ) : null
                  }
                  overlap="circular"
                >
                  <ShareIcon fontSize="small" />
                </Badge>
              </IconButton>
            </Tooltip>

            <Hidden only={['xs', 'sm']}>
              <Tooltip
                title={<Translate id="button.remove" />}
                placement="top"
                arrow
              >
                <IconButton onClick={handleDeleteClick}>
                  <DeleteOutlineIcon />
                </IconButton>
              </Tooltip>
            </Hidden>

            {currentClubUuid !== process.env.REACT_APP_DEFAULT_CLUB_UUID && (
              <Tooltip
                title={<Translate id="button.transfer-history" />}
                placement="top"
                arrow
              >
                <IconButton onClick={handlePlayerTransferHistoryIconClick}>
                  <HistoryIcon />
                </IconButton>
              </Tooltip>
            )}

            <SubLink
              to={`/team/${teamUuid}/season/${+seasonId}/current-team/menu`}
              isFullPath
            >
              <Button color="primary" size="large" className={classes.Cancel}>
                <Translate id="button.cancel" />
              </Button>
            </SubLink>
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={handleSave}
              className={classes.Save}
            >
              <Translate id="button.save" />
            </Button>
          </Box>
        </Grid>
      </Paper>
    </>
  )
}

TopBar.propTypes = {
  handleSave: PropTypes.func.isRequired,
  deletePlayer: PropTypes.func.isRequired,
  onShare: PropTypes.func.isRequired,
  history: PropTypes.shape().isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      seasonId: PropTypes.number.isRequired,
      teamUuid: PropTypes.string.isRequired,
      playerId: PropTypes.string.isRequired,
    }),
  }).isRequired,
  currentClubUuid: PropTypes.string.isRequired,
}

export default compose(
  withRouter,
  connect(
    ({ clubs: { current } }) => ({
      currentClubUuid: current.uuid,
    }),
    {}
  )
)(TopBar)
